'use client'

import { useCallback, useEffect, useState } from 'react'

/** GoTrue allows one sign-in email per address per 60 seconds. */
export const RESEND_COOLDOWN_SECONDS = 60

/** Seconds from GoTrue's "…only request this after 42 seconds." message. */
export function rateLimitSeconds(message: string): number | null {
  const match = message.match(/after (\d+) seconds?/)
  return match ? Number(match[1]) : null
}

/**
 * Countdown for the "send again" button. Without it people tap into GoTrue's
 * per-address limit and collect 429s (7 in a row for one person, 2026-10-12).
 */
export function useResendCooldown() {
  // A deadline, not a decrementing counter: background tabs (the person is in
  // their mail app) throttle timers, and a counter would freeze there.
  const [deadline, setDeadline] = useState(0)
  const [secondsLeft, setSecondsLeft] = useState(0)

  useEffect(() => {
    if (!deadline) return
    const update = () => {
      const left = Math.max(0, Math.ceil((deadline - Date.now()) / 1000))
      setSecondsLeft(left)
      if (left === 0) setDeadline(0)
    }
    update()
    const timer = setInterval(update, 1000)
    document.addEventListener('visibilitychange', update)
    return () => {
      clearInterval(timer)
      document.removeEventListener('visibilitychange', update)
    }
  }, [deadline])

  const start = useCallback((seconds: number = RESEND_COOLDOWN_SECONDS) => {
    setDeadline(Date.now() + seconds * 1000)
    setSecondsLeft(seconds)
  }, [])

  return { secondsLeft, start }
}
